import React, { ReactElement } from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { useListContext } from 'framework/list';
import AddTimelineItemInput from '../AddTimelineItemInput';
import { TimelineContextData } from '../Timeline';
import { ResourceData } from 'framework/crud';
import { type TimelineItem } from '../types';
import { useShowContext } from 'framework/show';
import { useUiTheme } from 'framework/uiTheme';
import { Fade as DefaultFade } from 'framework/ui/mui-builder';
import BreadcrumbsWithMenu from '../../layout/BreadcrumbsWithMenu/BreadcrumbsWithMenu';
import {
  getShowPageContentInnerSx,
  pageContentSx,
  showPageEntityHeaderWrapperSx,
  showPageHeaderWrapperSx,
  showPagePaperSx,
} from '../../layout/pageLayoutSx';
import { topButtonsSx } from 'framework/ui/layout/ShowPageLayout/topButtonsSx';

const DEFAULT_FUNCTION = () => {};

interface TimelinePageLayoutProps {
  breadcrumbs?: React.ReactNode;
  menu?: React.ReactNode;
  children?: React.ReactNode;
  entityHeader?: React.ReactNode;
  filter?: React.ReactNode;
  title?: string;
  titleVariant?: 'h1' | 'h2' | 'h4';
  hasAddForm?: boolean;
  additionalButtons?: ReactElement[];
}

const TimelinePageLayout = ({
  breadcrumbs,
  menu,
  children,
  entityHeader,
  filter,
  title,
  titleVariant,
  hasAddForm = true,
  additionalButtons,
}: TimelinePageLayoutProps) => {
  const { Fade: ThemedFade } = useUiTheme();
  const Fade = ThemedFade || DefaultFade;
  const {
    registries = [],
    addForm,
    setAddForm = DEFAULT_FUNCTION,
    createTimelineItemMethod = DEFAULT_FUNCTION,
    addToList = DEFAULT_FUNCTION,
    isEntryViewMode,
    reloadData,
    wrapItem = () => ({}),
  }: TimelineContextData = useListContext();
  const { data: parentContext } = useShowContext();

  const closeAddForm = () => setAddForm(null);

  return (
    <Paper elevation={0} data-hj-suppress sx={showPagePaperSx}>
      <BreadcrumbsWithMenu breadcrumbs={breadcrumbs} menu={menu} />
      {entityHeader && (
        <Box sx={showPageHeaderWrapperSx}>
          <Box sx={showPageEntityHeaderWrapperSx}>{entityHeader}</Box>
        </Box>
      )}
      <Box sx={{ display: { xs: 'block', md: 'flex' } }}>
        {!!menu && (
          <Box
            sx={{
              mb: '25px',
              ml: { xs: -2, md: '-19px' },
              mr: { xs: -2, md: '35px' },
              flexShrink: 0,
              width: { md: '215px' },
              display: { xs: 'none', md: 'block' },
            }}
          >
            {menu}
          </Box>
        )}
        <Box sx={getShowPageContentInnerSx(false)}>
          <Box>
            <Box
              sx={theme => ({
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                mb: filter ? '18px' : '2.25rem',
                [theme.breakpoints.down('sm')]: { display: 'block' },
              })}
            >
              <Typography
                variant={titleVariant ?? (entityHeader ? 'h2' : 'h1')}
                color={titleVariant === 'h4' ? 'primary' : 'brand'}
              >
                {title || 'Timeline'}
              </Typography>

              <Stack
                sx={[
                  topButtonsSx,
                  theme => ({
                    [theme.breakpoints.down('sm')]: { mt: '10px' },
                  }),
                ]}
              >
                {hasAddForm && !addForm && (
                  <AddTimelineItemInput
                    registries={registries}
                    selectItemCallback={registry => {
                      const { AddForm, source, urlPostfix, additionalAfterAddSubmit } = registry;

                      const form = (
                        <AddForm
                          onCancel={closeAddForm}
                          submitFormFunc={(data: ResourceData) => createTimelineItemMethod(urlPostfix, data)}
                          afterSubmit={{
                            execute: (data: TimelineItem) => {
                              addToList(wrapItem(data, source));
                              closeAddForm();
                              if (additionalAfterAddSubmit) {
                                additionalAfterAddSubmit(data, parentContext, reloadData);
                              }
                            },
                          }}
                        />
                      );
                      setAddForm(form);
                    }}
                  />
                )}
                {additionalButtons}
              </Stack>
            </Box>
            <Fade in={!!filter} timeout={20}>
              <Box mb={1}>{isEntryViewMode ? null : filter}</Box>
            </Fade>
          </Box>
          <Box sx={pageContentSx}>{children}</Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default TimelinePageLayout;
